import { useAuth } from "@/hooks/useAuth";
import { useDeviceType } from "@/hooks/useDeviceType";
import { getFeedbackCategories, submitFeedback } from "@/services/widgets";
import { selectSelectedRole } from "@/store/userSlice";
import { useMsal } from "@azure/msal-react";
import React, { useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";


interface FeedbackCategory {
  Id: number;
  CategoryName: string;
}

interface FeedbackFormProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const { account } = useAuth();
  const selectedRole = useSelector(selectSelectedRole);
  const displayName =
    accounts[0]?.name || account?.displayName;
  const { isMobile, isTablet } = useDeviceType();
  const userPrincipalName = accounts[0]?.username || account?.userPrincipalName || "";
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [message, setMessage] = useState<{
  type: "success" | "error";
  text: string;
} | null>(null);

  useEffect(() => {
    if (!open) return;

    const loadCategories = async () => {
      try {
        const data = await getFeedbackCategories(selectedRole);
        setCategories(data || []);
      } catch (error) {
        console.error(error);
      }
    };

    loadCategories();
  }, [open]);

const handleSubmit = async () => {
  setMessage(null);

  if (!selectedCategory) {
    setMessage({
      type: "error",
      text: t("feedbackForm.categoryError") ,
    });
    return;
  }

  if (!description.trim()) {
    setMessage({
      type: "error",
      text: t("feedbackForm.descriptionError"),
    });
    return;
  }

  try {
    setIsSubmitting(true);

    const selectedCategoryName =
      categories.find(
        (category) => category.Id === Number(selectedCategory)
      )?.CategoryName || "";

await submitFeedback({
  role : selectedRole,
  userName: displayName,
  categoryName: selectedCategoryName,
  description: description.trim(),
  submittedBy: userPrincipalName,
});


    setMessage({
      type: "success",
      text: t("feedbackForm.successMessage"),
    });

    setSelectedCategory("");
    setDescription("");

    setTimeout(() => {
      onClose();
      setMessage(null);
    }, 1500);
  } catch (error) {
    console.error(error);

    setMessage({
      type: "error",
      text: t("feedbackForm.errorMessage"),
    });
  } finally {
    setIsSubmitting(false);
  }
};

  if (!open) return null;

  return (
    <div
  className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex items-center justify-center bg-black/50"
>
  <div
    className={`
      bg-white shadow-xl
      ${
        isMobile
          ? "w-full h-full rounded-none p-5 overflow-y-auto"
          : isTablet
          ? "w-full max-w-2xl rounded-xl p-6"
          : "w-full max-w-lg rounded-xl p-6"
      }
    `}
  >
        <h2 className="mb-6 text-2xl font-semibold text-gray-900">
          {t("feedbackForm.title")}
        </h2>
        {message && (
  <div
    className={`mb-4 rounded-lg px-4 py-3 text-sm font-medium ${
      message.type === "success"
        ? "bg-green-100 text-green-800 border border-green-300"
        : "bg-red-100 text-red-800 border border-red-300"
    }`}
  >
    {message.text}
  </div>
)}

        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t("feedbackForm.categoryLabel")}
          </label>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="
              w-full
              rounded-lg
              border
              border-gray-300
              bg-white
              px-4
              py-3
              text-gray-900
              focus:border-orange-500
              focus:outline-none
              focus:ring-2
              focus:ring-orange-200
            "
          >
            <option value="" className="text-gray-900">
              {t("feedbackForm.dropdownLabel")}
            </option>

            {categories.map((category) => (
              <option
                key={category.Id}
                value={category.Id}
                className="text-gray-900 bg-white"
              >
                {category.CategoryName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t("feedbackForm.descriptionLabel")}
          </label>

          <textarea
            rows={isMobile ? 8 : 5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("feedbackForm.descriptionPlaceholder")}
            className="
              w-full
              rounded-lg
              border
              border-gray-300
              px-4
              py-3
              text-gray-900
              placeholder:text-gray-400
              focus:border-orange-500
              focus:outline-none
              focus:ring-2
              focus:ring-orange-200
            "
          />
        </div>

       <div
  className={`gap-3 ${
    isMobile
      ? "flex flex-col"
      : "flex justify-end"
  }`}
>
          <button
  onClick={onClose}
  className="
    rounded-lg
    border
    border-gray-300
    px-3
    py-2
    text-gray-700
    hover:bg-gray-50
    w-full md:w-auto
  "
>
            {t("feedbackForm.cancelButton")}
          </button>

          <button
  onClick={handleSubmit}
  disabled={isSubmitting}
  className="
    rounded-lg
    bg-[#D35400]
    px-3
    py-2
    text-white
    hover:bg-[#B94700]
    disabled:cursor-not-allowed
    disabled:opacity-50
    w-full md:w-auto
  "
>
  {isSubmitting ? t("feedbackForm.submitting"): t("feedbackForm.submitButton")}
</button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;